import { isUndefined } from '@silo/utils';
import { useSiloWeb3 } from '@silo/web3';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ContractTransaction } from 'ethers';
import { FC, ReactNode } from 'react';
import useNotify from '../../../hooks/useNotify';
import { claimRewards } from './claimRewards';
import { BalanceDisplay, ClaimSuccess } from './components';
import { totalRewardsBalanceKey } from './useQueryTotalRewardsBalance';
import {
  BigIntWithDecimals,
  ChecksumAddress,
  ProtocolInstance,
  ProtocolInstanceValue,
  createProtocolInstanceValueGetter,
  isEnvVarOn,
} from '@silo/types';
import { getRewardsTokenAddress, getRewardsTokenDecimals, getRewardsTokenSymbol } from './config';
import { Button, Dots } from '@silo/ui';

interface ClaimRewardsFormProps {
  totalBalance: bigint;
  totalBalanceUSD?: BigIntWithDecimals;
  siloIncentivesControllerAddress?: ChecksumAddress;
  shareTokenAddresses: ChecksumAddress[];
  protocolInstance: ProtocolInstance;
}

export const ClaimRewardsForm: FC<ClaimRewardsFormProps> = ({
  totalBalance,
  totalBalanceUSD,
  siloIncentivesControllerAddress,
  shareTokenAddresses,
  protocolInstance,
}) => {
  const { clientProvider, signingProvider, account } = useSiloWeb3();
  const { notifyError } = useNotify();
  const queryClient = useQueryClient();

  const {
    isLoading: isPending,
    mutate,
    isSuccess,
    data,
  } = useMutation<ContractTransaction, Error>({
    mutationFn: async () => {
      if (
        isUndefined(siloIncentivesControllerAddress) ||
        isUndefined(clientProvider) ||
        isUndefined(signingProvider) ||
        isUndefined(account)
      ) {
        throw new Error('claimRewards params undefined');
      }

      return claimRewards({
        siloIncentivesControllerAddress,
        signingProvider,
        clientProvider,
        account,
        shareTokenAddresses,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries([totalRewardsBalanceKey]);
    },
    onError: (error) => {
      notifyError(error.message);
    },
  });

  if (isSuccess) {
    return <ClaimSuccess data={data} />;
  }

  const { headingOverride } = getModalConfig(protocolInstance);

  return (
    <div className="flex flex-col gap-8 sm:gap-6">
      <h2 className="text-xl font-medium">{headingOverride ?? 'Claim and HODL!'}</h2>

      <BalanceDisplay
        protocolKey={protocolInstance}
        rewardTokenAddress={getRewardsTokenAddress(protocolInstance)}
        rewardTokenSymbol={getRewardsTokenSymbol(protocolInstance)}
        rewardTokenDecimals={getRewardsTokenDecimals(protocolInstance)}
        balance={totalBalance}
        balanceUsd={totalBalanceUSD?.value}
      />

      <div>
        <Button variant="primary" disabled={isPending} className="w-full md:w-fit" onClick={() => mutate()}>
          {isPending ? <Dots>Pending</Dots> : 'Claim'}
        </Button>
      </div>
    </div>
  );
};

interface ModalConfig {
  headingOverride?: ReactNode;
}

const isArbitrumArbRewardsEnabled = isEnvVarOn(() => process.env.NEXT_PUBLIC_ARBITRUM_ARB_REWARDS);

const MODAL_CONFIG: ProtocolInstanceValue<ModalConfig> = {
  mainnetLegacy: {},
  mainnetLlama: {
    headingOverride: 'Claim your earned CRV rewards',
  },
  mainnetGrand: {
    headingOverride: 'Claim your SILO rewards',
  },
  arbitrum: {
    headingOverride: isArbitrumArbRewardsEnabled ? 'Claim your ARB rewards here!' : 'Claim your SILO rewards',
  },
  optimism: {
    headingOverride: 'Claim your OP rewards',
  },
  base: {
    headingOverride: 'Claim your rewards',
  },
  lrtbase: {
    headingOverride: 'Claim your rewards',
  },
  local: {},
  anvil: {},
};

const getModalConfig = createProtocolInstanceValueGetter({}, MODAL_CONFIG);
